/**
 * Every word the new look shows for CompBox's things, in one place
 * (docs/new-look/PLAN.md, step 0.3).
 *
 * These are words on screen only. Code, the API and the database keep their
 * names: an Idea is an EpicRequest, a Blueprint is an Epic, a Part is a Feature.
 */

interface Noun {
  one: string;
  many: string;
}

export const WORDS = {
  /** EpicRequest */
  idea: { one: "Idea", many: "Ideas" },
  /** Epic */
  blueprint: { one: "Blueprint", many: "Blueprints" },
  /** Feature */
  part: { one: "Part", many: "Parts" },
  /** Task */
  task: { one: "Task", many: "Tasks" },
  /** Pull request */
  pullRequest: { one: "Pull request", many: "Pull requests" },
  /** Machine (a computer running the CompBox app) */
  machine: { one: "Machine", many: "Machines" },
  /** Team */
  team: { one: "Team", many: "Teams" },
  /** Child team that holds a repository */
  project: { one: "Project", many: "Projects" },
  /** Physical product (hardware + firmware) */
  device: { one: "Device", many: "Devices" },
} as const satisfies Record<string, Noun>;

export type NounKey = keyof typeof WORDS;

/** The AI helpers, by what they do for the user. */
export const HELPERS = {
  /** request-formulator: interviews the user until the idea is clear */
  ideaHelper: "Idea helper",
  /** planner: turns a clear idea into a blueprint */
  blueprintMaker: "Blueprint maker",
  /** orchestrator and its workers: builds the blueprint on the user's machine */
  builder: "Builder",
} as const;

/** The stages an idea moves through, in order. */
export const STAGES = {
  idea: "Idea",
  blueprint: "Blueprint",
  build: "Build",
  done: "Done",
} as const;

export type Stage = keyof typeof STAGES;
export const STAGE_ORDER: readonly Stage[] = ["idea", "blueprint", "build", "done"];

/** Main menu, in order (desktop). */
export const MENU = {
  dashboard: "Dashboard",
  teamBoard: "Team board",
  ideas: "Ideas",
  blueprints: "Blueprints",
  devices: "Devices",
  connections: "Connections",
  teams: "Teams",
  projects: "Projects",
  settings: "Settings",
  /** Phone tab bar */
  home: "Home",
  more: "More",
} as const;

/** "1 idea", "3 ideas", "0 ideas". */
export function countOf(n: number, noun: NounKey): string {
  const word = n === 1 ? WORDS[noun].one : WORDS[noun].many;
  return `${String(n)} ${word.toLowerCase()}`;
}
